/**
 * Registry mapping backend warehouse types to display metadata (label + brand
 * icon) and the canonical type used throughout the v2 account flow.
 *
 * The backend normalizes destination names (REDSHIFT→RS, DELTALAKE→DATABRICKS,
 * etc.); `backendTypes` lists every alias that resolves to a given canonical key
 * so we can both filter warehouses and look up an account's display metadata.
 */
import type * as React from 'react';

import {
  BigQueryIcon,
  ClickHouseIcon,
  DatabricksIcon,
  GenericWarehouseIcon,
  PostgresIcon,
  RedshiftIcon,
  SnowflakeIcon,
} from './icons/WarehouseTypeIcon';

export type WarehouseTypeKey = 'SNOWFLAKE' | 'RS' | 'DATABRICKS' | 'BQ' | 'POSTGRES' | 'CLICKHOUSE';

export interface WarehouseTypeMeta {
  /** Canonical key for the type */
  key: WarehouseTypeKey;

  label: string;

  /** Brand icon component (accepts `className`). */
  Icon: React.ComponentType<{ className?: string }>;
  /** Every backend `type` string that maps to this canonical key. */
  backendTypes: string[];
}

/** The warehouse types offered in the "Add account" picker, in display order. */
export const SUPPORTED_WAREHOUSE_TYPES: WarehouseTypeMeta[] = [
  { key: 'SNOWFLAKE', label: 'Snowflake', Icon: SnowflakeIcon, backendTypes: ['SNOWFLAKE'] },
  { key: 'RS', label: 'Redshift', Icon: RedshiftIcon, backendTypes: ['RS', 'REDSHIFT'] },
  { key: 'DATABRICKS', label: 'Databricks', Icon: DatabricksIcon, backendTypes: ['DATABRICKS', 'DELTALAKE'] },
  { key: 'BQ', label: 'BigQuery', Icon: BigQueryIcon, backendTypes: ['BQ', 'BIGQUERY'] },
  { key: 'POSTGRES', label: 'Postgres', Icon: PostgresIcon, backendTypes: ['POSTGRES'] },
  { key: 'CLICKHOUSE', label: 'ClickHouse', Icon: ClickHouseIcon, backendTypes: ['CLICKHOUSE'] },
];

/** Title-case an unknown backend type for a readable fallback label (POSTGRES → Postgres). */
function prettifyType(type: string): string {
  if (!type) return 'Warehouse';
  const lower = type.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** Display-only metadata (label + brand icon) for *any* backend warehouse type. */
export interface WarehouseTypeDisplay {
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}

export function isKnownWarehouseType(backendType: string): boolean {
  const upper = (backendType ?? '').toUpperCase();
  return SUPPORTED_WAREHOUSE_TYPES.some((meta) => meta.backendTypes.includes(upper));
}

/**
 * Resolve a backend warehouse `type` to its display metadata. Unknown types
 * get a generic database icon and a title-cased label so they
 * still render gracefully in the account list and view drawer.
 *
 * Returns display-only metadata (no canonical `key`): unsupported types have no
 * `WarehouseTypeKey`, and display consumers only need the label + icon.
 */
export function warehouseTypeMeta(backendType: string): WarehouseTypeDisplay {
  const upper = (backendType ?? '').toUpperCase();
  const found = SUPPORTED_WAREHOUSE_TYPES.find((m) => m.backendTypes.includes(upper));
  if (found) return found;
  return { label: prettifyType(backendType), Icon: GenericWarehouseIcon };
}

/**
 * Fold any backend warehouse type onto its canonical `WarehouseTypeKey`, or `null` when none claims
 * it.
 *
 * **Two spellings reach this screen for one engine, and comparing them raw silently drops a whole
 * population.** A connection row carries the DRIVER string the save sent (`BIGQUERY`, `REDSHIFT`,
 * `DELTALAKE`); `listRsAccountsOverview` reports the server's CANONICAL type (`BQ`, `RS`,
 * `DATABRICKS`). `'BQ' !== 'BIGQUERY'`, so an adopt picker that compared them directly offered no
 * BigQuery candidate at all — 849 of prod's 893 warehouse accounts, which is to say the feature.
 *
 * `backendTypes` already lists every alias per key, so this adds no table: it inverts the one that
 * exists. Canonical spellings are themselves members of their own `backendTypes`, which makes this
 * idempotent — feeding it either side's spelling is safe, and that is what lets both sides be folded
 * rather than one side being "the" side.
 */
export function canonicalWarehouseTypeKey(backendType: string | null | undefined): WarehouseTypeKey | null {
  const upper = (backendType ?? '').toUpperCase();
  return SUPPORTED_WAREHOUSE_TYPES.find((m) => m.backendTypes.includes(upper))?.key ?? null;
}

export interface AccountDetailField {
  label: string;
  /** Candidate keys in the sanitized RS config, in priority order (first present wins). */
  keys: string[];
}

/**
 * The read-only "account detail" fields shown for a destination — the same
 * prefilled, non-secret fields each form renders read-only. Keyed to the
 * sanitized RudderStack config so same-type destinations are distinguishable.
 */
export function accountDetailFields(type: string): AccountDetailField[] {
  const upper = type.toUpperCase();
  if (upper === 'SNOWFLAKE') {
    return [
      { label: 'Account', keys: ['account'] },
      { label: 'Database', keys: ['database'] },
      { label: 'Warehouse', keys: ['warehouse'] },
    ];
  }
  if (upper === 'RS' || upper === 'REDSHIFT') {
    return [
      { label: 'Host', keys: ['host'] },
      { label: 'Port', keys: ['port'] },
      { label: 'Database', keys: ['database'] },
    ];
  }
  if (upper === 'POSTGRES') {
    return [
      { label: 'Host', keys: ['host'] },
      { label: 'Port', keys: ['port'] },
      { label: 'Database', keys: ['database'] },
    ];
  }
  if (upper === 'CLICKHOUSE') {
    return [
      { label: 'Host', keys: ['host'] },
      { label: 'Port', keys: ['port'] },
      { label: 'Default database', keys: ['database'] },
      { label: 'Cluster', keys: ['cluster'] },
    ];
  }
  if (upper === 'BQ' || upper === 'BIGQUERY') {
    return [
      { label: 'Project', keys: ['project'] },
      { label: 'Location', keys: ['location'] },
    ];
  }
  if (upper === 'DATABRICKS' || upper === 'DELTALAKE') {
    return [
      { label: 'Host', keys: ['host'] },
      { label: 'HTTP path', keys: ['httpPath', 'path'] },
    ];
  }
  return [];
}

/**
 * The bucket-identity dims (host-dim + db-dim only) shown in the Add-account
 * bucket picker. Deliberately excludes Warehouse (the per-connection compute
 * choice, picked later) and Schema (the optional narrowing field, entered in the
 * configure step) — a bucket is identified solely by `(host, db)`.
 *
 * Returns one label per dim, in `[hostDim, dbDim]` order; BigQuery collapses to a
 * single `Project` dim (host-dim === db-dim).
 */
export function bucketIdentityFields(type: string): string[] {
  const upper = type.toUpperCase();
  if (upper === 'SNOWFLAKE') return ['Account', 'Database'];
  if (upper === 'RS' || upper === 'REDSHIFT') return ['Host', 'Database'];
  if (upper === 'POSTGRES') return ['Host', 'Database'];
  if (upper === 'CLICKHOUSE') return ['Host', 'Default database'];
  if (upper === 'DATABRICKS' || upper === 'DELTALAKE') return ['Host', 'Catalog'];
  if (upper === 'BQ' || upper === 'BIGQUERY') return ['Project'];
  return ['Host', 'Database'];
}

/**
 * One free-text field in the Add-account manual entry panel (phase 1.5, AI-1370).
 *
 * `dim` says which half of the bucket identity the field feeds. BigQuery returns a single
 * field with `dim: 'both'` because its project is simultaneously the host dim and the db dim
 * (`BIGQUERY.extractDims` returns `{ host: project, db: project }`).
 */
export interface ManualEntryField {
  dim: 'host' | 'db' | 'both';
  label: string;
  placeholder: string;
  /** Rendered with an "(optional)" marker — only Databricks' catalog, whose db dim may be empty. */
  optional?: boolean;
}

/**
 * Per-type labels for the manually typed bucket coordinates. These are the same two dims the
 * bucket picker shows via {@link bucketIdentityFields}, worded as an instruction to the customer
 * rather than as a column header ("Account identifier", not "Account"), and carrying the
 * placeholder that tells them which shape of string the driver expects.
 *
 * This list is the *whole* of step 2, deliberately (AI-1370). The dims are what
 * `deriveAccountKeyFromDims` turns into the account key, and that is the step's entire job. Every
 * other value a family needs — Snowflake's compute warehouse, Databricks' HTTP path, Redshift's
 * port — is per-connection config and belongs on step 3, where it has always lived and where a
 * synced destination's stored config prefills it. Asking for one here would ask for it twice.
 */
export function manualEntryFields(type: WarehouseTypeKey): ManualEntryField[] {
  switch (type) {
    case 'SNOWFLAKE':
      return [
        { dim: 'host', label: 'Account identifier', placeholder: 'xy12345.us-east-1' },
        { dim: 'db', label: 'Database', placeholder: 'ANALYTICS' },
      ];
    case 'RS':
      return [
        { dim: 'host', label: 'Host or cluster id', placeholder: 'my-cluster.abc123.us-east-1.redshift.amazonaws.com' },
        { dim: 'db', label: 'Database', placeholder: 'analytics' },
      ];
    case 'DATABRICKS':
      return [
        { dim: 'host', label: 'Workspace host', placeholder: 'dbc-12345678.cloud.databricks.com' },
        { dim: 'db', label: 'Catalog', placeholder: 'main', optional: true },
      ];
    case 'BQ':
      return [{ dim: 'both', label: 'Project id', placeholder: 'my-gcp-project' }];
    case 'CLICKHOUSE':
      return [
        { dim: 'host', label: 'Host', placeholder: 'abc123xyz.us-east-1.aws.clickhouse.cloud' },
        { dim: 'db', label: 'Default database', placeholder: 'analytics' },
      ];
    case 'POSTGRES':
    default:
      return [
        { dim: 'host', label: 'Host', placeholder: 'db.example.com' },
        { dim: 'db', label: 'Database', placeholder: 'analytics' },
      ];
  }
}
